import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import ResourceListItem from './ResourceListItem';
import FilterControl from './Filters/FilterControl';
import { Provider } from './Context';
import {
  Button,
  Flex,
  Spinner,
} from '..';
import CheckableButton from './CheckableButton';

const propTypes = {
  filterControl: PropTypes.node,

  // Name of the resource, such as Plans or Forms.
  resourceName: PropTypes.shape({
    singular: PropTypes.string,
    plural: PropTypes.string,
  }),
  // Each item is passed to renderItem.
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  // Collection of ID's for the corrently selected items.
  selectedItems: PropTypes.arrayOf(PropTypes.number),
  // Overlays item list with a spinner while a background action is being performed.
  loading: PropTypes.bool,
  // Call back function when a selection is changed.
  onSelectionChange: PropTypes.func,
  // Function to rendewr each list item.
  renderItem: PropTypes.func.isRequired,
  // Maximum of two actions that can be given to manupulate the list items.
  promotedBulkActions: PropTypes.arrayOf(PropTypes.object),
};
const defaultProps = {
  resourceName: undefined,
  selectedItems: [],
  loading: false,
  promotedBulkActions: undefined,
  onSelectionChange: undefined,
  filterControl: undefined,
};

const ResourceListWrapper = styled.div`
  position: relative;
`;

const ResourceListUl = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;
const Header = styled.div`
  padding: ${props => props.theme.space_stack_sm};
  border-bottom: .1rem solid ${props => props.theme.borderColor};
`;
const OverLaySpinner = styled.div`
  position: absolute;
  top: 0;
  left:0;
  height: 100%;
  width: 100%;
  background-color: white;
  opacity: 0.5;
  z-index: 1000;
`;
const FlexItem = styled(Flex.Item)`
  margin-left: 0;
  margin-right: 0;
  padding: 0;
`;

export default class ResourceList extends Component {
  getName = (selectMode) => {
    const { selectedItems, resourceName, items } = this.props;
    if (selectMode) {
      return selectedItems.length > 1 ? resourceName.plural : resourceName.singular;
    }
    return items.length > 1 ? resourceName.plural : resourceName.singular;
  }

  handleHeaderCheckboxChange = ({ target: { checked } }) => {
    const { onSelectionChange, items } = this.props;
    const allItems = [];
    if (checked) {
      // Add all the id into the state.
      items.forEach((item) => {
        allItems.push(item.id);
      });
    }
    onSelectionChange(allItems);
  }

  handleHeaderCheckboxLabelText = () => {
    const { selectedItems, items, loading } = this.props;
    if (loading) {
      return `Loading ${this.getName(false)}...`;
    }
    if (selectedItems.length > 0) {
      return `${selectedItems.length} selected`;
    }
    return `Showing ${items && items.length} ${this.getName(false)}`;
  }

  handleGetContext = () => {
    const {
      selectedItems,
      promotedBulkActions,
      resourceName,
    } = this.props;
    return {
      selectedItems,
      onSelectionChange: this.handleSelectionChange,
      promotedBulkActions,
      resourceName,
    };
  }

  handleSelectionChange = (selected, id) => {
    const {
      onSelectionChange,
      selectedItems,
    } = this.props;

    const newlySelectedItems = [...selectedItems];
    if (selected) {
      newlySelectedItems.push(id);
    } else {
      newlySelectedItems.splice(newlySelectedItems.indexOf(id), 1);
    }

    if (onSelectionChange) {
      onSelectionChange(newlySelectedItems);
    }
  }

  static FilterControl = FilterControl;

  static Item = ResourceListItem;

  render() {
    const {
      items,
      selectedItems,
      renderItem,
      promotedBulkActions,
      filterControl,
      loading,
    } = this.props;

    const filterMarkup = filterControl ? (<div>{filterControl}</div>) : null;

    const listMarkup = (items || []).map(item => renderItem(item));

    const loadingOverlay = loading && (
      <OverLaySpinner>
        <Spinner />
      </OverLaySpinner>
    );


    let headerMarkup;
    if (promotedBulkActions && promotedBulkActions.length > 0) {
      let promotedBulkActionsMarkup;
      if (selectedItems.length > 0) {
        promotedBulkActionsMarkup = promotedBulkActions.map(action => (
          <Button onClick={action.onAction} key={action.content} size="medium">
            {action.content}
          </Button>
        ));
      }

      headerMarkup = (
        <Header>
          <Flex alignItems="center">
            <FlexItem padding="sm">
              <CheckableButton
                label={this.handleHeaderCheckboxLabelText()}
                indeterminate={(selectedItems.length !== 0)
                  && (items.length !== selectedItems.length)}
                size="medium"
                checked={items.length === selectedItems.length}
                onChange={this.handleHeaderCheckboxChange}
                checkableButton={selectedItems.length > 0}
              />
            </FlexItem>
            <FlexItem>
              {promotedBulkActionsMarkup}
            </FlexItem>
          </Flex>
        </Header>
      );
    }

    return (
      <Provider value={this.handleGetContext()}>
        {filterMarkup}
        <ResourceListWrapper>
          {headerMarkup}
          <ResourceListUl>
            {listMarkup}
            {loadingOverlay}
          </ResourceListUl>
        </ResourceListWrapper>
      </Provider>
    );
  }
}
ResourceList.propTypes = propTypes;
ResourceList.defaultProps = defaultProps;
